import React, { useMemo, useRef, useState } from "react";
import type { ComponentProps } from "react";
import { GaraazPlaceEnquiryForm } from "@svan-garaaz/reactsharedlib";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import Services from "./services";
import useNotification from "../../utils/useNotification";
import globalConstants from "../../utils/constants";
import PageScaffold from "../../components/PageScaffold";
import { useGetOrderMediaConfig } from "./queryHooks";
import { buildTestPrefillDefaults } from "./testPrefillConfig";
import "./PlaceOrderFormTest.css";

type GaraazPlaceEnquiryFormValues = Parameters<
  NonNullable<ComponentProps<typeof GaraazPlaceEnquiryForm>["onSubmit"]>
>[0];

/** Hide challan/extra columns Orca does not use on place enquiry. */
const PLACE_ENQUIRY_FIELDS: NonNullable<
  ComponentProps<typeof GaraazPlaceEnquiryForm>["fields"]
> = {
  workshopCode: { isVisible: false },
  associatedBranchName: { isVisible: false },
  mrp: { isVisible: false },
  dateOfPurchase: { isVisible: false },
  totalAmount: { isVisible: false },
};

const PlaceOrderFormTest: React.FC = () => {
  const [formKey, setFormKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTestOrder, setIsTestOrder] = useState(false);
  const [isPreFilling, setIsPreFilling] = useState(false);
  const isTestOrderRef = useRef(false);
  const initiatorIdRef = useRef(
    JSON.parse(localStorage.getItem(globalConstants.ORCA_USER) ?? "{}")._id,
  );
  const toast = useNotification();
  const authToken = localStorage.getItem(globalConstants.AUTH_TOKEN) ?? "";
  const { data: mobileConfig, isLoading: isMobileConfigLoading } =
    useGetOrderMediaConfig();

  const typesenseConfig = useMemo(() => {
    const rawHost = mobileConfig?.typesense_host?.trim();
    const apiKey = mobileConfig?.typesense_api_key?.trim();
    if (!rawHost || !apiKey) return null;
    const host = rawHost.replace(/^https?:\/\//i, "").split("/")[0];
    if (!host) return null;
    return { host, apiKey };
  }, [mobileConfig?.typesense_host, mobileConfig?.typesense_api_key]);

  const toggleTestOrder = () => {
    setIsTestOrder((prev) => {
      isTestOrderRef.current = !prev;
      return !prev;
    });
  };

  const buildPayload = (
    values: GaraazPlaceEnquiryFormValues,
    testOrder: boolean,
  ) => {
    const user = JSON.parse(
      localStorage.getItem(globalConstants.ORCA_USER) ?? "{}",
    );
    const { insuranceClaimId, spocDetails, surveyorDetails, ...restBilling } =
      values.billingDetails ?? {};
    return {
      customerId: values.selectedCustomer._id,
      initiatorId: user._id,
      customerRemarks: values.remarks,
      shipmentAddress: values.customerAddress,
      orderDate: new Date(values.orderDateTime),
      orderItems: values.partsTableData.map((p) => ({
        partId: p._id,
        orderedQuantity: p.orderedQuantity,
      })),
      orderMedia:
        values.media.length > 0
          ? values.media.map((m) => m.mediaId)
          : undefined,
      carMaker: values.carMaker || undefined,
      licensePlateNumber: values.licensePlateNumber || undefined,
      carModel: values.carModel || undefined,
      vinNumber: values.vinNumber || undefined,
      billingDetails:
        Object.keys(restBilling).length > 0 ? restBilling : undefined,
      insuranceClaimId: insuranceClaimId || undefined,
      ...(spocDetails &&
      (spocDetails.name?.trim() ||
        spocDetails.mobileNumber?.trim() ||
        spocDetails.estimateApprovalFormUrl?.trim())
        ? { spocDetails }
        : {}),
      ...(surveyorDetails &&
      (surveyorDetails.name?.trim() || surveyorDetails.mobileNumber?.trim())
        ? { surveyorDetails }
        : {}),
      testOrder,
    };
  };

  const handleSubmit = async (values: GaraazPlaceEnquiryFormValues) => {
    if (!values.selectedCustomer) {
      toast({ description: "Please select a customer.", status: "error" });
      return;
    }
    if (!values.customerAddress) {
      toast({
        description: "Please select a delivery address.",
        status: "error",
      });
      return;
    }
    if (!values.orderDateTime) {
      toast({
        description: "Please set an order date and time.",
        status: "error",
      });
      return;
    }
    if (!values.carMaker) {
      toast({ description: "Please select a car maker.", status: "error" });
      return;
    }
    const hasContent =
      (values.remarks ?? "").trim().length > 0 ||
      values.media.length > 0 ||
      values.partsTableData.length > 0;
    if (!hasContent) {
      toast({
        description: "Please add at least one part, media, or a remark.",
        status: "error",
      });
      return;
    }
    const hasZeroQty = values.partsTableData.some(
      (p) => p.orderedQuantity <= 0,
    );
    if (hasZeroQty) {
      toast({
        description: "All parts must have a quantity greater than zero.",
        status: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildPayload(values, isTestOrderRef.current);
      const order = await Services.placeGaraazOrder(payload);
      if (order) {
        toast({
          description: `${isTestOrderRef.current ? "[TEST] " : ""}Enquiry (${order.orderNo}) placed successfully.`,
          status: "success",
        });
        setFormKey((k) => k + 1);
        setIsTestOrder(false);
        isTestOrderRef.current = false;
      } else {
        toast({
          description: "We're unable to place your enquiry. Please try again.",
          status: "error",
        });
      }
    } catch {
      toast({
        description: "We're unable to place your enquiry. Please try again.",
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreFillAndPlace = async () => {
    setIsPreFilling(true);
    const defaults = buildTestPrefillDefaults(
      mobileConfig?.orca_test_order_prefill_config ?? null,
    );

    if (!defaults.customerId || !defaults.partId) {
      toast({
        description:
          "Pre-fill config is missing or invalid. Add orca_test_order_prefill_config in mobile config.",
        status: "error",
      });
      setIsPreFilling(false);
      return;
    }
    if (!defaults.shipmentAddress) {
      toast({
        description:
          "Pre-fill config must include shipmentAddress in orca_test_order_prefill_config.",
        status: "error",
      });
      setIsPreFilling(false);
      return;
    }

    const payload = {
      customerId: defaults.customerId,
      initiatorId: initiatorIdRef.current,
      customerRemarks: defaults.remarks,
      shipmentAddress: defaults.shipmentAddress,
      billingDetails: defaults.billingDetails,
      orderDate: defaults.orderDate,
      orderItems: [{ partId: defaults.partId, orderedQuantity: 1 }],
      carMaker: defaults.carMaker,
      testOrder: true,
    };

    try {
      const order = await Services.placeGaraazOrder(payload);
      if (order) {
        toast({
          description: `[TEST] Enquiry (${order.orderNo}) auto-placed via pre-fill.`,
          status: "success",
        });
      } else {
        toast({
          description: "Pre-fill placement failed. Please try again.",
          status: "error",
        });
      }
    } catch {
      toast({
        description:
          "Pre-fill placement failed. Verify mobile config and customer/part IDs.",
        status: "error",
      });
    } finally {
      setIsPreFilling(false);
    }
  };

  return (
    <PageScaffold title="Place Enquiry" isHeaderVisible={false}>
      <div className="orca-place-enquiry-form">
        {/* ── Action bar ── */}
        <Box
          mb={4}
          p={3}
          borderRadius="lg"
          bg={isTestOrder ? "orange.50" : "gray.50"}
          border="1px solid"
          borderColor={isTestOrder ? "orange.200" : "gray.200"}
          transition="all 0.2s"
        >
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack spacing={3} align="center">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Order Mode:
              </Text>
              <Tooltip
                label={
                  isTestOrder
                    ? "This order will be marked as a test order"
                    : "Click to mark this as a test order"
                }
                placement="top"
                hasArrow
              >
                <Button
                  size="sm"
                  variant={isTestOrder ? "solid" : "outline"}
                  colorScheme={isTestOrder ? "orange" : "gray"}
                  onClick={toggleTestOrder}
                  leftIcon={
                    <Text as="span" fontSize="14px">
                      {isTestOrder ? "🧪" : "📋"}
                    </Text>
                  }
                  borderRadius="full"
                  px={4}
                  fontWeight="semibold"
                >
                  {isTestOrder ? "Test Order: ON" : "Test Order: OFF"}
                </Button>
              </Tooltip>
              {isTestOrder && (
                <Badge
                  colorScheme="orange"
                  variant="subtle"
                  fontSize="0.7rem"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  ORDER WILL BE FLAGGED AS TEST
                </Badge>
              )}
            </HStack>

            <Divider
              orientation="vertical"
              height="28px"
              display={{ base: "none", md: "block" }}
            />

            <Tooltip
              label="Instantly place a test order using orca_test_order_prefill_config from mobile config"
              placement="top"
              hasArrow
            >
              <Button
                size="sm"
                colorScheme="purple"
                variant="outline"
                onClick={handlePreFillAndPlace}
                isLoading={isPreFilling || isMobileConfigLoading}
                isDisabled={isMobileConfigLoading}
                loadingText="Placing…"
                leftIcon={
                  <Text as="span" fontSize="14px">
                    ⚡
                  </Text>
                }
                borderRadius="full"
                px={4}
                fontWeight="semibold"
                _hover={{ bg: "purple.50" }}
              >
                Pre-Fill &amp; Place
              </Button>
            </Tooltip>
          </HStack>
        </Box>

        <GaraazPlaceEnquiryForm
          key={formKey}
          authToken={authToken}
          apiBaseUrl={process.env.REACT_APP_BASE_URL ?? ""}
          typesenseConfig={typesenseConfig}
          addCountryCodeToSearch
          fields={PLACE_ENQUIRY_FIELDS}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onNotification={toast}
          useSharedPartsSearch
          enablePartSearchHighlighting
        />
      </div>
    </PageScaffold>
  );
};

export default PlaceOrderFormTest;
